import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  ArrowRight,
  CircleMinus,
  ExternalLink,
  FileSpreadsheet,
  SquareArrowOutDownRight,
  Users,
} from "lucide-react";

import { Tooltip } from "@mui/material";

import { UIButton, UIChip, UIStatusTag } from "../../ui";
import { clx } from "../../../utils/Helpers";
import NationalityMix from "../NationalityMix";
import "../dashboardSection.scss";
import "./OngoingEventCard.scss";

const formatCount = (value) => Number(value).toLocaleString("en-US");
const ICON_SIZE = 14;

/** Metric rows in display order. Missing values render as "--". */
const METRIC_ROWS = [
  { key: "attendanceRate", Icon: Users, isRate: true },
  { key: "checkIn", Icon: ExternalLink },
  { key: "lateCheckIn", Icon: ExternalLink },
  { key: "checkOut", Icon: SquareArrowOutDownRight },
  { key: "noShow", Icon: CircleMinus, negative: true },
];

const FooterLink = ({ label, onClick, cta }) => (
  <UIButton
    variant="text"
    className={cta ? "dashboard-cta" : "dashboard-link"}
    endIcon={<ArrowRight size={ICON_SIZE} strokeWidth={2} />}
    onClick={onClick}
  >
    {label}
  </UIButton>
);

FooterLink.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  cta: PropTypes.bool,
};

FooterLink.defaultProps = {
  cta: false,
};

/**
 * One ongoing event on the dashboard: name + payment-period tag, attendance
 * metrics, nationality mix, and a footer of View Timesheet / Reports actions.
 */
const OngoingEventCard = ({
  name,
  paymentPeriod,
  paymentPeriodLabel,
  metrics,
  metricLabels,
  applicants,
  newApplicantsPercent,
  newApplicantsLabel,
  nationality,
  nationalityCopy,
  dateRange,
  openReportsCount,
  footerCopy,
  onViewTimesheet,
  onViewReports,
}) => {
  // Controlled so a tap toggles the name tooltip on touch devices (no hover).
  const [nameTooltipOpen, setNameTooltipOpen] = useState(false);

  return (
    <article className="ongoing-event-card">
      <header className="ongoing-event-card__header">
        <Tooltip
          title={name}
          placement="top"
          arrow
          open={nameTooltipOpen}
          onOpen={() => setNameTooltipOpen(true)}
          onClose={() => setNameTooltipOpen(false)}
        >
          <h3
            className="ongoing-event-card__name"
            onClick={() => setNameTooltipOpen((value) => !value)}
          >
            {name}
          </h3>
        </Tooltip>
        <UIStatusTag
          preset="table"
          status={paymentPeriod}
          label={paymentPeriodLabel}
          className="ongoing-event-card__period"
        />
      </header>

      <ul className="ongoing-event-card__metrics">
        {METRIC_ROWS.filter(
          ({ key }) => !(key === "lateCheckIn" && paymentPeriod === "daily"),
        ).map(({ key, Icon, isRate, negative }) => {
          const raw = metrics[key];
          let value;
          if (raw == null) {
            value = "--";
          } else if (isRate) {
            value = (
              <>
                <span>{raw}</span>
                <span>%</span>
              </>
            );
          } else {
            value = formatCount(raw);
          }
          return (
            <li
              key={key}
              className={clx(
                "ongoing-event-card__metric",
                isRate && "ongoing-event-card__metric--rate",
                negative && "ongoing-event-card__metric--negative",
              )}
            >
              <span className="ongoing-event-card__metric-icon">
                <Icon size={ICON_SIZE} strokeWidth={1.75} />
              </span>
              <span className="ongoing-event-card__metric-label">
                {metricLabels[key]}
              </span>
              <span className="ongoing-event-card__metric-value">{value}</span>
            </li>
          );
        })}
      </ul>

      {applicants != null && (
        <div className="ongoing-event-card__applicants">
          <span className="ongoing-event-card__applicants-text">
            <span className="ongoing-event-card__applicants-icon">
              <Users size={ICON_SIZE} strokeWidth={1.75} />
            </span>
            {nationalityCopy.applicantsLabel.replace(
              "{count}",
              formatCount(applicants),
            )}
          </span>
          <UIChip
            appearance="light"
            size="small"
            color="warning"
            label={
              <>
                <span>
                  <span>{newApplicantsPercent ?? 0}</span>
                  <span>%</span>
                </span>
                <span>{newApplicantsLabel}</span>
              </>
            }
            className="ongoing-event-card__new-applicants"
          />
        </div>
      )}

      <NationalityMix
        label={nationalityCopy.label}
        applicantsLabel={nationalityCopy.applicantsLabel}
        saudiLabel={nationalityCopy.saudiLabel}
        nonSaudiLabel={nationalityCopy.nonSaudiLabel}
        count={nationality.count}
        saudiPercent={nationality.saudiPercent}
        nonSaudiPercent={nationality.nonSaudiPercent}
      />

      <footer className="ongoing-event-card__footer">
        <div className="ongoing-event-card__footer-row">
          <span className="ongoing-event-card__footer-text">{dateRange}</span>
          <FooterLink
            label={footerCopy.viewTimesheet}
            onClick={onViewTimesheet}
          />
        </div>

        <hr className="ongoing-event-card__divider" />

        <div className="ongoing-event-card__footer-group">
          <div className="ongoing-event-card__footer-row">
            <span className="ongoing-event-card__footer-text">
              <FileSpreadsheet size={ICON_SIZE} strokeWidth={1.75} />
              {openReportsCount > 0
                ? footerCopy.openReports.replace("{count}", openReportsCount)
                : footerCopy.noReports}
            </span>
            {openReportsCount > 0 && (
              <FooterLink
                cta
                label={footerCopy.viewReports}
                onClick={onViewReports}
              />
            )}
          </div>
        </div>
      </footer>
    </article>
  );
};

OngoingEventCard.propTypes = {
  name: PropTypes.string.isRequired,
  paymentPeriod: PropTypes.oneOf(["daily", "monthly"]).isRequired,
  paymentPeriodLabel: PropTypes.string.isRequired,
  metrics: PropTypes.shape({
    attendanceRate: PropTypes.number,
    checkIn: PropTypes.number,
    lateCheckIn: PropTypes.number,
    checkOut: PropTypes.number,
    noShow: PropTypes.number,
  }).isRequired,
  metricLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  applicants: PropTypes.number,
  newApplicantsPercent: PropTypes.number,
  newApplicantsLabel: PropTypes.string,
  nationality: PropTypes.shape({
    count: PropTypes.number,
    saudiPercent: PropTypes.number,
    nonSaudiPercent: PropTypes.number,
  }).isRequired,
  nationalityCopy: PropTypes.shape({
    label: PropTypes.string,
    applicantsLabel: PropTypes.string,
    saudiLabel: PropTypes.string,
    nonSaudiLabel: PropTypes.string,
  }).isRequired,
  dateRange: PropTypes.string.isRequired,
  openReportsCount: PropTypes.number,
  footerCopy: PropTypes.shape({
    viewTimesheet: PropTypes.string,
    viewReports: PropTypes.string,
    openReports: PropTypes.string,
    noReports: PropTypes.string,
  }).isRequired,
  onViewTimesheet: PropTypes.func.isRequired,
  onViewReports: PropTypes.func.isRequired,
};

OngoingEventCard.defaultProps = {
  applicants: null,
  newApplicantsPercent: null,
  newApplicantsLabel: "",
  openReportsCount: 0,
};

export default OngoingEventCard;
